import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSchool } from '../context/SchoolContext';
import {
    ArrowLeft, User, Phone, Mail, MapPin,
    CreditCard, BookOpen, Calendar, History,
    CheckCircle, AlertCircle, TrendingUp, Pencil, Save, X, Upload, Plus, Trash, UserPlus, ExternalLink, FileText, Printer
} from 'lucide-react';
import './Customers.css';
import './DetailPages.css';

const CustomerDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const config = window.reactPress || window.rpVars || {};
    const API_BASE = (config.api?.rest_url || '/wp-json').replace(/\/$/, '') + '/sdt/v1';

    const {
        customers, financials, instructors, packages, branches, lessons,
        stages, wpUsers, getPackageName, getInstallmentName, getBranchName,
        updateCustomer, createUserAccount
    } = useSchool();

    const customer = customers.find(c => String(c.id) === String(id));
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isCreatingUser, setIsCreatingUser] = useState(false);
    const [userMessage, setUserMessage] = useState(null);

    useEffect(() => {
        if (customer && !editData) {
            setEditData({
                firstName: customer.first_name,
                surname: customer.last_name,
                email: customer.email_address,
                phone: customer.phone_number,
                address: customer.address,
                idNumber: customer.national_id__no,
                packageId: customer.package_id || customer.package,
                branchId: customer.branch_id,
                instructorId: customer.instructor_id,
                stage: customer.stage,
                documents: customer.documents || [],
                nextOfKin: customer.nextOfKin || { name: '', relationship: '', phone: '' },
                lessonsRemaining: customer.lessonsRemaining || '',
                enrollmentDate: customer.enrollmentDate || '',
                dateOfBirth: customer.dateOfBirth || '',
                notes: customer.notes || '',
                balance: customer.balance || 0,
                userLogin: customer.user_login || '',
                isBalanceOverridden: false
            });
        }
    }, [customer, editData]);

    if (!customer) {
        return (
            <div className="page-container">
                <button className="btn-link flex items-center gap-2 mb-4" onClick={() => navigate('/customers')}>
                    <ArrowLeft size={18} /> Back to Customers
                </button>
                <div className="card text-center py-20">
                    <AlertCircle size={48} className="text-red mx-auto mb-4" />
                    <h2>Customer Not Found</h2>
                    <p className="text-secondary">The student record you are looking for does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
    const customerPayments = (financials.payments || []).filter(p =>
        String(p.customerId) === String(customer.student_id_number) ||
        String(p.customerId) === String(id)
    );

    // Lessons Calculation
    const studentLessons = lessons.filter(l =>
        String(l.student_id) === String(customer.student_id_number) ||
        String(l.customer_id) === String(id)
    );
    const selectedPackage = packages.find(p => String(p.id) === String(editData?.packageId || customer.package_id || customer.package));
    const packageLessons = selectedPackage?.lessonsCount || 0;
    const completedLessons = studentLessons.length;
    const calculatedRemaining = Math.max(0, packageLessons - completedLessons);

    const handleSave = async () => {
        if (editData.isBalanceOverridden && !editData.notes.trim()) {
            alert('A reason is required when manually overriding the balance.');
            return;
        }

        setIsSaving(true);
        try {
            await updateCustomer(id, editData);
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving customer:', error);
            alert('Failed to save changes.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateAccount = async () => {
        setIsCreatingUser(true);
        setUserMessage(null);
        try {
            const result = await createUserAccount(id);
            setUserMessage({ type: 'success', text: `Account created! Username: ${result.username}, Password: ${result.password}` });
        } catch (error) {
            setUserMessage({ type: 'error', text: error.message });
        } finally {
            setIsCreatingUser(false);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            // Upload to VP REST API (requires X-WP-Nonce if using cookie auth, or just open for now/cookie)
            // Using SchoolContext API_BASE. 
            // Note: Since we are using standard fetch in Context which includes nonce/headers, we try to use that or raw fetch if complex.
            // Raw fetch with WP nonce is safest.

            // To get headers from Context, we might need to expose them or just presume 'headers' variable isn't available here directly 
            // unless we grab token/nonce. 
            // Let's assume standard cookie auth works or we need to pass the nonce.
            // Usually ReactPress passes a global nonce.

            const nonce = window.sdt_api?.nonce || '';
            const res = await fetch(`${API_BASE}/customer/upload`, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': nonce
                },
                body: formData
            });

            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message || 'Upload failed');
            }

            const data = await res.json();

            const newDoc = {
                name: file.name,
                url: data.url,
                date: new Date().toISOString(),
                type: data.type
            };

            setEditData(prev => ({
                ...prev,
                documents: [...(prev.documents || []), newDoc]
            }));

        } catch (error) {
            console.error("Upload error:", error);
            alert("Failed to upload file: " + error.message);
        }
    };

    const printReceipt = (payment) => {
        const receiptWindow = window.open('', '_blank', 'width=800,height=600');
        if (!receiptWindow) return;

        const branchName = customer.branch || getBranchName(customer.branch_id) || 'Head Office';

        receiptWindow.document.write(`
            <html>
                <head>
                    <title>Payment Receipt - ${payment.id}</title>
                    <style>
                        body { font-family: 'Helvetica Neue', Arial, sans-serif; padding: 40px; color: #333; }
                        .header { text-align: center; margin-bottom: 40px; border-bottom: 2px solid #eee; padding-bottom: 20px; }
                        .logo { font-size: 24px; font-weight: bold; color: #b45309; }
                        .branch { font-size: 14px; color: #666; margin-top: 5px; }
                        .details { margin-bottom: 30px; }
                        .row { display: flex; justify-content: space-between; margin-bottom: 10px; }
                        .label { font-weight: bold; color: #666; }
                        .amount { font-size: 24px; font-weight: bold; color: #16a34a; text-align: right; margin-top: 20px; border-top: 1px solid #eee; pt: 20px; }
                        .footer { margin-top: 50px; text-align: center; font-size: 12px; color: #999; }
                        .btn-print { display: block; margin: 20px auto; padding: 10px 20px; background: #333; color: #fff; border: none; cursor: pointer; }
                        @media print { .btn-print { display: none; } }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <div class="logo">Safety Driver Training</div>
                        <div class="branch">${branchName} Branch</div>
                    </div>
                    
                    <div class="details">
                        <h3>Payment Receipt</h3>
                        <div class="row"><span class="label">Date:</span> <span>${new Date(payment.payment_date || payment.date).toLocaleString()}</span></div>
                        <div class="row"><span class="label">Receipt ID:</span> <span>${payment.id}</span></div>
                        <div class="row"><span class="label">Student:</span> <span>${customerName} (${customer.student_id_number || customer.id})</span></div>
                        <div class="row"><span class="label">Payment Method:</span> <span>${payment.method}</span></div>
                        <div class="row"><span class="label">Description:</span> <span>${payment.description || 'Tuition Fees'}</span></div>
                    </div>

                    <div class="amount">
                        Amount Paid: $${parseFloat(payment.amount).toFixed(2)}
                    </div>

                    <div class="footer">
                        <p>Thank you for your business!</p>
                        <p>Keep this receipt for your records.</p>
                        <button class="btn-print" onclick="window.print()">Print Receipt</button>
                    </div>
                </body>
            </html>
        `);
        receiptWindow.document.close();
    };

    return (
        <div className="page-container">
            <header className="page-header">
                <div className="flex items-center gap-4">
                    <button className="f-close-btn" onClick={() => navigate('/customers')}>
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h2 className="page-title">{customerName}</h2>
                        <p className="page-subtitle">Student ID: {customer.student_id_number || customer.id}</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    {isEditing ? (
                        <>
                            <button className="btn btn-secondary flex items-center gap-2" onClick={() => setIsEditing(false)}>
                                <X size={18} /> Cancel
                            </button>
                            <button className="btn btn-primary flex items-center gap-2" onClick={handleSave} disabled={isSaving}>
                                <Save size={18} /> {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </>
                    ) : (
                        <button className="btn btn-primary flex items-center gap-2" onClick={() => setIsEditing(true)}>
                            <Pencil size={18} /> Edit Profile
                        </button>
                    )}
                </div>
            </header>

            <div className="detail-grid">
                {/* Profile Card / Personal Details */}
                <div className="card profile-overview-card">
                    <div className="profile-header">
                        <div className="customer-avatar large">
                            {customerName.charAt(0)}
                        </div>
                        <div className="flex-1">
                            {isEditing ? (
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        placeholder="First Name"
                                        value={editData?.firstName || ''}
                                        onChange={e => setEditData({ ...editData, firstName: e.target.value })}
                                        className="f-input"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Surname"
                                        value={editData?.surname || ''}
                                        onChange={e => setEditData({ ...editData, surname: e.target.value })}
                                        className="f-input"
                                    />
                                </div>
                            ) : (
                                <h3>{customerName}</h3>
                            )}
                            <div className="flex items-center gap-2 text-gold font-bold">
                                <TrendingUp size={16} />
                                {isEditing ? (
                                    <select
                                        value={editData?.stage || ''}
                                        onChange={e => setEditData({ ...editData, stage: e.target.value })}
                                        className="f-input"
                                    >
                                        {stages.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                ) : (
                                    <span>{customer.stage || 'Initial Stage'}</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="info-list mt-6">
                        <div className="info-item">
                            <Phone size={18} />
                            {isEditing ? (
                                <input
                                    type="tel"
                                    value={editData?.phone || ''}
                                    onChange={e => setEditData({ ...editData, phone: e.target.value })}
                                    className="f-input"
                                />
                            ) : (
                                <span>{customer.phone || customer.phone_number || 'N/A'}</span>
                            )}
                        </div>
                        <div className="info-item">
                            <Mail size={18} />
                            {isEditing ? (
                                <input
                                    type="email"
                                    value={editData?.email || ''}
                                    onChange={e => setEditData({ ...editData, email: e.target.value })}
                                    className="f-input"
                                />
                            ) : (
                                <span>{customer.email || 'No email provided'}</span>
                            )}
                        </div>
                        <div className="info-item">
                            <MapPin size={18} />
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={editData?.address || ''}
                                    onChange={e => setEditData({ ...editData, address: e.target.value })}
                                    className="f-input"
                                />
                            ) : (
                                <span>{customer.address || 'Address not set'}</span>
                            )}
                        </div>
                        <div className="info-item border-top pt-4 mt-4">
                            <User size={18} />
                            <div className="flex-1">
                                <p className="text-xs text-secondary">NATIONAL ID</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData?.idNumber || ''}
                                        onChange={e => setEditData({ ...editData, idNumber: e.target.value })}
                                        className="f-input"
                                    />
                                ) : (
                                    <p className="font-medium">{customer.national_id__no || customer.idNumber || 'N/A'}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Training & Portal Status */}
                <div className="flex flex-col gap-6">
                    {/* Training Details */}
                    <div className="card">
                        <h4 className="flex items-center gap-2 mb-4">
                            <BookOpen size={20} className="text-gold" /> Training Status
                        </h4>
                        <div className="info-grid">
                            <div className="info-block">
                                <label>Assigned Branch</label>
                                {isEditing ? (
                                    <select
                                        value={editData?.branchId || ''}
                                        onChange={e => setEditData({ ...editData, branchId: e.target.value })}
                                        className="f-input"
                                    >
                                        <option value="">Select Branch</option>
                                        {branches.map(b => <option key={b.branch_id} value={b.branch_name}>{b.branch_name}</option>)}
                                    </select>
                                ) : (
                                    <p>{customer.branch || getBranchName(customer.branch_id) || 'Primary Branch'}</p>
                                )}
                            </div>
                            <div className="info-block">
                                <label>Course Package</label>
                                {isEditing ? (
                                    <select
                                        value={editData?.packageId || ''}
                                        onChange={e => setEditData({ ...editData, packageId: e.target.value })}
                                        className="f-input"
                                    >
                                        <option value="">Select Package</option>
                                        {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                ) : (
                                    <div>
                                        <p className="font-bold">{getPackageName(customer.packageId || customer.package)}</p>
                                        <p className="text-xs text-secondary italic">
                                            {packages.find(p => p.id === (customer.packageId || customer.package))?.package_details || 'No details available'}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <div className="info-block">
                                <label>Assigned Instructor</label>
                                {isEditing ? (
                                    <select
                                        value={editData?.instructorId || ''}
                                        onChange={e => setEditData({ ...editData, instructorId: e.target.value })}
                                        className="f-input"
                                    >
                                        <option value="">Select Instructor</option>
                                        {instructors.map(i => <option key={i.instructor_id} value={i.instructor_id}>{i.first_name} {i.last_name}</option>)}
                                    </select>
                                ) : (
                                    <p>{instructors.find(i => i.instructor_id === customer.instructor_id) ? `${instructors.find(i => i.instructor_id === customer.instructor_id).first_name} ${instructors.find(i => i.instructor_id === customer.instructor_id).last_name}` : 'Not Assigned'}</p>
                                )}
                            </div>
                            <div className="info-block">
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-black text-slate-800">{calculatedRemaining}</span>
                                        <span className="text-xs text-secondary font-medium uppercase tracking-wider">Remaining</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs text-secondary/70 border-t pt-2 mt-1">
                                        <span>Package Total: <strong className="text-slate-700">{packageLessons}</strong></span>
                                        <span>Completed: <strong className="text-green-600">{completedLessons}</strong></span>
                                    </div>
                                    {isEditing && (
                                        <div className="mt-2 pt-2 border-t border-dashed">
                                            <label className="text-[10px] uppercase font-bold text-secondary mb-1 block">Manual Override</label>
                                            <input
                                                type="number"
                                                value={editData?.lessonsRemaining || ''}
                                                onChange={e => setEditData({ ...editData, lessonsRemaining: e.target.value })}
                                                className="f-input text-xs"
                                                placeholder="Override..."
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="info-block">
                                <label>Enrollment Date</label>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        value={editData?.enrollmentDate || ''}
                                        onChange={e => setEditData({ ...editData, enrollmentDate: e.target.value })}
                                        className="f-input"
                                    />
                                ) : (
                                    <p>{customer.enrollmentDate || 'N/A'}</p>
                                )}
                            </div>
                            <div className="info-block">
                                <label>Date of Birth</label>
                                {isEditing ? (
                                    <input
                                        type="date"
                                        value={editData?.dateOfBirth || ''}
                                        onChange={e => setEditData({ ...editData, dateOfBirth: e.target.value })}
                                        className="f-input"
                                    />
                                ) : (
                                    <p>{customer.dateOfBirth || 'N/A'}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Next of Kin Card */}
                    <div className="card">
                        <h4 className="flex items-center gap-2 mb-4">
                            <User size={20} className="text-gold" /> Next of Kin
                        </h4>
                        <div className="info-grid">
                            <div className="info-block">
                                <label>Relationship</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={editData?.nextOfKin?.relationship || ''}
                                        onChange={e => setEditData({ ...editData, nextOfKin: { ...editData.nextOfKin, relationship: e.target.value } })}
                                        className="f-input"
                                    />
                                ) : (
                                    <p>{customer.nextOfKin?.relationship || 'N/A'}</p>
                                )}
                            </div>
                            <div className="info-block">
                                <label>Phone Number</label>
                                {isEditing ? (
                                    <input
                                        type="tel"
                                        value={editData?.nextOfKin?.phone || ''}
                                        onChange={e => setEditData({ ...editData, nextOfKin: { ...editData.nextOfKin, phone: e.target.value } })}
                                        className="f-input"
                                    />
                                ) : (
                                    <p>{customer.nextOfKin?.phone || 'N/A'}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Portal Status Card */}
                    <div className="card">
                        <h4 className="flex items-center gap-2 mb-4">
                            <ExternalLink size={20} className="text-gold" /> System Access
                        </h4>
                        {isEditing ? (
                            <div className="flex flex-col gap-2">
                                <label className="text-xs text-secondary">LINKED PORTAL ACCOUNT</label>
                                <select
                                    value={editData?.userLogin || ''}
                                    onChange={e => setEditData({ ...editData, userLogin: e.target.value })}
                                    className="f-input"
                                >
                                    <option value="">(None)</option>
                                    {wpUsers.map(u => (
                                        <option key={u.ID} value={u.user_login}>
                                            {u.user_login} (ID: {u.ID})
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-secondary italic">Select the WordPress user account to link this student to the portal.</p>
                            </div>
                        ) : customer.userId || customer.user_login ? (
                            <div className="flex items-center gap-3 text-green">
                                <CheckCircle size={20} />
                                <div>
                                    <p className="font-bold">Student Portal Active</p>
                                    <p className="text-xs opacity-75">Username: {customer.user_login || 'Linked'}</p>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <p className="text-sm text-secondary mb-4">This student does not have a portal account yet. Creating one will allow them to view their schedule and progress.</p>
                                {userMessage && (
                                    <div className={`alert ${userMessage.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
                                        {userMessage.text}
                                    </div>
                                )}
                                <button
                                    className="btn btn-secondary w-full flex items-center justify-center gap-2"
                                    onClick={handleCreateAccount}
                                    disabled={isCreatingUser}
                                >
                                    <UserPlus size={18} /> {isCreatingUser ? 'Creating Account...' : 'Create Student Account'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Financial Status Card */}
                <div className="card stats-grid-mini">
                    <div className="stat-box-mini">
                        <p className="label">TOTAL FEES</p>
                        <p className="value">${parseFloat(selectedPackage?.price || 0).toFixed(2)}</p>
                    </div>
                    <div className="stat-box-mini">
                        <p className="label text-green">PAID</p>
                        <p className="value text-green">${customerPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}</p>
                    </div>
                    <div className="stat-box-mini">
                        <p className="label text-red">BALANCE</p>
                        {isEditing ? (
                            <div className="flex flex-col gap-2">
                                <p className="value text-red">
                                    ${(parseFloat(selectedPackage?.price || 0) - customerPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)).toFixed(2)}
                                </p>
                                <div className="flex items-center gap-2 text-xs">
                                    <input
                                        type="checkbox"
                                        checked={editData.isBalanceOverridden}
                                        onChange={e => setEditData({ ...editData, isBalanceOverridden: e.target.checked })}
                                        id="balanceOverride"
                                    />
                                    <label htmlFor="balanceOverride">Manual Override</label>
                                </div>
                                {editData.isBalanceOverridden && (
                                    <input
                                        type="number"
                                        className="f-input text-xs"
                                        placeholder="Enter new balance"
                                        value={editData.balance}
                                        onChange={e => setEditData({ ...editData, balance: e.target.value })}
                                    />
                                )}
                            </div>
                        ) : (
                            <p className="value text-red">
                                ${(parseFloat(selectedPackage?.price || 0) - customerPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)).toFixed(2)}
                            </p>
                        )}
                    </div>
                    <div className="stat-box-mini">
                        <p className="label">PLAN</p>
                        <p className="value text-sm">{selectedPackage?.name || getInstallmentName(customer.paymentPlan)}</p>
                    </div>
                </div>

                {isEditing && editData.isBalanceOverridden && (
                    <div className="card">
                        <label className="block mb-2 font-bold text-red flex items-center gap-2">
                            <AlertCircle size={16} /> ADJUSTMENT REASON (REQUIRED)
                        </label>
                        <textarea
                            className="f-input w-full"
                            rows="3"
                            placeholder="Please explain why the balance is being manually adjusted..."
                            required
                            value={editData.notes}
                            onChange={e => setEditData({ ...editData, notes: e.target.value })}
                        ></textarea>
                    </div>
                )}

                {/* Documents Section */}
                <div className="card">
                    <div className="flex justify-between items-center mb-4">
                        <h4 className="flex items-center gap-2">
                            <History size={20} className="text-gold" /> Student Documents
                        </h4>
                        <div>
                            <input
                                type="file"
                                id="doc-upload"
                                className="hidden"
                                onChange={handleFileUpload}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            />
                            <label htmlFor="doc-upload" className="btn btn-secondary btn-sm flex items-center gap-1 cursor-pointer">
                                <Plus size={14} /> Upload Doc
                            </label>
                        </div>
                    </div>
                    <div className="doc-grid grid grid-cols-1 md:grid-cols-2 gap-3">
                        {((isEditing ? editData : customer)?.documents || []).length === 0 ? (
                            <div className="col-span-full text-center py-8 text-secondary/60 bg-secondary/5 rounded-xl border border-dashed">
                                <FileText size={40} className="mx-auto mb-2 opacity-20" />
                                <p className="text-sm">No documents uploaded.</p>
                            </div>
                        ) : (
                            (isEditing ? editData : customer).documents.map((doc, idx) => (
                                <div key={idx} className="doc-card flex items-center justify-between p-3 bg-white hover:border-gold/50 transition-all rounded-xl border group">
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className="p-2 bg-gold/10 rounded-lg text-gold group-hover:bg-gold group-hover:text-white transition-colors">
                                            {doc.type?.includes('image') ? <History size={18} /> : <FileText size={18} />}
                                        </div>
                                        <div className="overflow-hidden">
                                            <p className="text-sm font-bold truncate w-full" title={doc.name}>{doc.name}</p>
                                            <p className="text-[10px] text-secondary">{doc.date ? new Date(doc.date).toLocaleDateString() : 'Uploaded Document'}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        <a href={doc.url} target="_blank" rel="noreferrer" className="p-2 hover:bg-gold/10 rounded-lg text-secondary hover:text-gold transition-colors" title="View Document">
                                            <ExternalLink size={16} />
                                        </a>
                                        {isEditing && (
                                            <button
                                                className="p-2 hover:bg-red/10 rounded-lg text-secondary hover:text-red transition-colors"
                                                title="Remove Document"
                                                onClick={() => {
                                                    const filtered = editData.documents.filter((_, i) => i !== idx);
                                                    setEditData({ ...editData, documents: filtered });
                                                }}
                                            >
                                                <Trash size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Payment History */}
                <div className="card span-full">
                    <h4 className="flex items-center gap-2 mb-6">
                        <CreditCard size={20} className="text-gold" /> Payment History
                    </h4>
                    {customerPayments.length === 0 ? (
                        <div className="text-center py-10 text-secondary border rounded-lg border-dashed">
                            No payment records found for this student.
                        </div>
                    ) : (
                        <div className="data-table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Receipt ID</th>
                                        <th>Method</th>
                                        <th>Description</th>
                                        <th className="text-right">Amount</th>
                                        <th className="text-right">Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customerPayments.sort((a, b) => new Date(b.payment_date || b.date) - new Date(a.payment_date || a.date)).map(p => (
                                        <tr key={p.id}>
                                            <td>{new Date(p.payment_date || p.date).toLocaleString()}</td>
                                            <td className="font-mono text-sm">{p.id}</td>
                                            <td>{p.method}</td>
                                            <td>{p.description || 'Fees Payment'}</td>
                                            <td className="text-right font-bold text-green">${parseFloat(p.amount).toFixed(2)}</td>
                                            <td className="text-right">
                                                <button className="icon-btn text-blue" onClick={() => printReceipt(p)} title="Print Receipt">
                                                    <Printer size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CustomerDetail;
